import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { PolicyValidatorErrors } from '.';
import { IGenericError, IErrorReturnService } from '../models/errors';
import {
  AprissFinalizeRequest,
  FraudDecision,
  ICreateReturnRequest,
  ICreateReturnResponse,
  Identifier,
  IRefundRequest,
  IRefundResponse,
  IShipmentType,
  ISorisFinalizeRequest,
  ISorisFinalizeResponse,
  IValidateAddressRequest,
  IValidateAddressResponse,
  IValidateZipCodeRequest,
  IValidateZipCodeResponse,
  OrderVersionRequest,
} from '../models/api';
import { RefundValues } from '../models/returns';
import {
  AprissFinalizeAPI,
  FinalizeSorisReturnAPI,
  OrderVersionAPI,
  RefundEstimationAPI,
  SSReturnCreateAPI,
  ValidateAddressAPI,
  ValidateZipCodeAPI,
} from '../api/returns';
import { TagApiError, TagFinalizeReturnApiError } from './analytics-tagging';
import { shipmentTypeToIdentifierMap } from '../constants/general';

/**
 * Safely checks the AxiosError for the Generic Error Model and returns the error data
 * @param error - The Axios error object.
 * @returns Returns the object inside the error (code, description and retryable) If none found, will return undefined
 */
export const getAPIErrorData = (
  error: AxiosError<IGenericError>,
): IErrorReturnService | undefined => {
  const errorData = error?.response?.data?.errors?.[0] || undefined;
  return errorData;
};

export const unknownError: IErrorReturnService = {
  code: 'UNKNOWN',
  description: 'Unknown Error',
  retryable: true,
};

/**
 * Checks if the given error is related to a multi-shipment address error.
 * This error should only occur during phase 1 of multi vendor address
 * @param error - The Axios error object.
 * @returns True if the error is a multi-shipment address error, false otherwise.
 */
export const isMultiShipmentAddressError = (
  error: AxiosError<IGenericError>,
) => {
  const errorCode = getAPIErrorData(error)?.code;
  return error?.response?.status === 400 && errorCode === 'MULTI_SHIPMENTS';
};

export const isProtectionPlanError = (error: AxiosError<IGenericError>) =>
  error?.status === 400 &&
  error?.response?.data?.errors?.[0]?.code === 'SSR_HRTVPOLICY_ERR_4102';

/**
 * Gets the PolicyValidator error type based on the API response.
 *
 * @param {AxiosError} error - The AxiosError object representing the HTTP error.
 * @returns {PolicyValidatorErrors} The type of error scenario that occurred.
 *
 * @description
 * This function checks the error message of Axios (created when we throw an error inside a Promise), while also checking
 * the error status code+body of the response to determine the appropriate scenario type.
 * If the error is a 5xx network error, it returns PolicyValidatorErrors.NETWORK.
 * If the error is related to multiple shipments (e.g., status 400 and specific error code),
 *  it returns PolicyValidatorErrors.MULTI_SHIPMENTS.
 * If HRTV validation contains some ineligible items, return PolicyValidatorErrors.INELIGIBLE_ITEMS
 * Otherwise, return PolicyValidatorErrors.GENERIC.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorType = (error: AxiosError<IGenericError>) => {
  const { message } = error;
  const code = getAPIErrorData(error)?.code;
  if (message === 'HAS_INELIGIBLE_ITEMS') {
    return PolicyValidatorErrors.HAS_INELIGIBLE_ITEMS;
  }
  if (code === 'SSR_UPS_ERR_1002') {
    return PolicyValidatorErrors.SSR_UPS_ERR_1002;
  }

  const isNetworkError =
    error?.response?.status === 500 ||
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ECONNABORTED';
  if (isNetworkError) return PolicyValidatorErrors.NETWORK;

  const isMultiShipmentError = isMultiShipmentAddressError(error);

  if (isMultiShipmentError) return PolicyValidatorErrors.MULTI_SHIPMENTS;

  return PolicyValidatorErrors.GENERIC;
};

export const fetchRefundEstimationAPI = (
  refundRequest: IRefundRequest,
): Promise<RefundValues | undefined> => {
  return RefundEstimationAPI(refundRequest)
    .then((response: AxiosResponse<IRefundResponse>) => {
      const { refund } = response.data;
      if (refund) {
        return refund;
      }
      throw new Error('RefundEstimationAPI: Empty 2xx Response');
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagApiError(error);
      return undefined;
    });
};

export const handleAddressValidateCall = (request: IValidateAddressRequest) =>
  ValidateAddressAPI(request).then(
    (response: AxiosResponse<IValidateAddressResponse>) => {
      return response.data;
    },
  );

export const handleZipCodeValidateCall = (request: IValidateZipCodeRequest) =>
  ValidateZipCodeAPI(request).then(
    (response: AxiosResponse<IValidateZipCodeResponse>) => {
      return response.data;
    },
  );

export const handleReturnCreateCall = (
  requestSsReturnCreate: ICreateReturnRequest,
  setIsCreateReturnLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<
    React.SetStateAction<IErrorReturnService | undefined>
  >,
) => {
  const temporaryRequest = {
    ...requestSsReturnCreate,
    order: { ...requestSsReturnCreate },
  };
  setIsCreateReturnLoading(true);
  return SSReturnCreateAPI(temporaryRequest)
    .then((response: AxiosResponse<ICreateReturnResponse>) => {
      const returnSuccessData = response?.data?.returnService;
      if (
        !returnSuccessData ||
        returnSuccessData.status !== 'success' ||
        !returnSuccessData.ssrCOMReturnServiceResponse
      ) {
        throw new Error('No return data found');
      }
      return returnSuccessData.ssrCOMReturnServiceResponse;
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      setError(getAPIErrorData(error) || unknownError);
    })
    .finally(() => {
      setIsCreateReturnLoading(false);
    });
};

export const handleMultiReturnCreateCall = (
  requestSsReturnCreate: ICreateReturnRequest,
) => {
  const temporaryRequest = {
    ...requestSsReturnCreate,
    order: { ...requestSsReturnCreate },
  };
  return SSReturnCreateAPI(temporaryRequest)
    .then((response: AxiosResponse<ICreateReturnResponse>) => {
      const returnSuccessData = response?.data?.returnService;
      if (
        !returnSuccessData ||
        returnSuccessData.status !== 'success' ||
        !returnSuccessData.ssrCOMReturnServiceResponse
      ) {
        throw new Error('No return data found');
      }
      return {
        data: returnSuccessData.ssrCOMReturnServiceResponse,
        error: null,
      };
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      return { error: getAPIErrorData(error) || unknownError, data: null };
    });
};

export const handleVersionIDCall = async (
  orderId: string,
  emailId: string,
  isGuest: boolean,
  net?: string,
): Promise<{
  versionId: string | null;
  error: IErrorReturnService | null;
}> => {
  try {
    const request: OrderVersionRequest = {
      orderId,
      emailId,
      isGuest,
      net,
    };
    const response = await OrderVersionAPI(request);
    if (!response.data?.orderVersion) {
      throw new Error('Version ID Fetch Error: No orderVersion found');
    }
    return { versionId: response.data.orderVersion, error: null };
  } catch (error) {
    // Tag on Order Version Error Here
    if ('response' in (error as AxiosError)) {
      const axiosError = error as AxiosError<IGenericError>;
      const errorData = getAPIErrorData(axiosError);
      return { error: errorData || unknownError, versionId: null };
    }
    return { error: unknownError, versionId: null };
  }
};

export const handleAprissCall = (request: AprissFinalizeRequest) => {
  return AprissFinalizeAPI(request).catch(() => {
    // TODO: tag an Apriss error here in case we get one maybe
    // TagFinalizeReturnApiError(error);
  });
};

export const handleSORISCreateCall = (request: ISorisFinalizeRequest) => {
  return FinalizeSorisReturnAPI(request)
    .then((response: AxiosResponse<ISorisFinalizeResponse>) => {
      const returnSuccessData = response?.data;
      if (!returnSuccessData) {
        throw new Error('No return data found');
      }
      return {
        data: returnSuccessData,
        error: null,
      };
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      return { error: getAPIErrorData(error) || unknownError, data: null };
    });
};

export const handleSORISFinalizeReturnCall = (
  request: ISorisFinalizeRequest,
  setIsCreateReturnLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<
    React.SetStateAction<IErrorReturnService | undefined>
  >,
) => {
  setIsCreateReturnLoading(true);
  return FinalizeSorisReturnAPI(request)
    .then((response: AxiosResponse<ISorisFinalizeResponse>) => {
      const returnSuccessData = response?.data;
      if (!returnSuccessData) {
        throw new Error('No return data found');
      }
      return returnSuccessData;
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      setError(getAPIErrorData(error) || unknownError);
    })
    .finally(() => {
      setIsCreateReturnLoading(false);
    });
};

/**
 * Determines the identifier to use for API calls based on fraud decisions and shipment type.
 * If there is only one fraud decision, returns '0' regardless of the actual identifier.
 * For multiple fraud decisions, returns the identifier that matches the shipment type.
 */
export const getIdentifier = (
  fraudDecisions: FraudDecision[],
  shipmentType: string,
): Identifier | undefined => {
  // If there is only one fraud decision, return '0'
  if (fraudDecisions.length === 1) {
    return '0';
  }

  // Otherwise, match the shipment type to the identifier
  return fraudDecisions.find(
    (decision) =>
      decision.identifier ===
      shipmentTypeToIdentifierMap[shipmentType as IShipmentType],
  )?.identifier;
};
